import React, { useState, useEffect } from 'react';
import { getProjects, createProject, deleteProject, getPMList, getAllProjectsAlerts, getAllDependencyAlerts } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingState from '../components/LoadingState';
import DashboardKpiRow from '../components/dashboard/DashboardKpiRow';
import ProjectCard from '../components/dashboard/ProjectCard';
import ProjectTable from '../components/dashboard/ProjectTable';
import { SegmentedControl, Icon, StateScreen } from '../components/ui';
import useConfirm from '../hooks/useConfirm';

// Token-based modal shell — matches the warm-cream + sage theme used by the
// Pre-Construction / Team / Settings modals (var(--) tokens + dark-mode aware),
// replacing the legacy navy .modal/.form-group styling this modal used to use.
const ModalShell = ({ title, subtitle, onClose, busy, children, footer, width = 620 }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center',
        background: 'oklch(0.2 0.008 100 / 0.34)', backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)', padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width, maxWidth: '100%', background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)', borderRadius: 14,
          boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)',
          overflow: 'hidden', fontFamily: 'var(--font-sans)', color: 'var(--text-1)',
          letterSpacing: '-0.005em', display: 'flex', flexDirection: 'column',
          maxHeight: '90%', minHeight: 0,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{title}</h2>
            {subtitle && <p style={{ margin: '5px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5 }}>{subtitle}</p>}
          </div>
          <button
            type="button" onClick={() => !busy && onClose()} aria-label="Close"
            style={{
              width: 26, height: 26, borderRadius: 6, color: 'var(--text-3)', flex: '0 0 auto',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: '1px solid transparent', cursor: busy ? 'default' : 'pointer',
            }}
          >
            <Icon name="x" size={14} />
          </button>
        </header>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px' }}>{children}</div>
        {footer && (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
};

const ProjectList = ({ onSelectProject }) => {
  const { confirm, confirmDialogProps } = useConfirm();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Double-submit guards. `creatingProject` covers the new-project modal,
  // `deletingId` is per-project so a slow delete only freezes its own
  // delete control (the dialog is double-confirmed already, but a fast
  // typed-confirm + Enter could fire DELETE twice before the first
  // returns).
  const [creatingProject, setCreatingProject] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [projectAlerts, setProjectAlerts] = useState({});
  const [depAlerts, setDepAlerts] = useState({});
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('projectViewMode') || 'tiles');
  const [projectNumberError, setProjectNumberError] = useState('');
  const [pmList, setPMList] = useState([]);
  const [formData, setFormData] = useState({
    project_name: '',
    project_number: '',
    client_name: '',
    original_contract_value: '',
    start_date: '',
    end_date: '',
    project_manager: '',
    construction_manager: '',
    salesperson: '',
    project_developer: '',
    billing_frequency: 'Monthly',
    default_retainage_percent: '',
    description: '',
    client_visible: true,
    // ce_integration_enabled is a one-time setting fixed at
    // creation. Default ON because the sales-tool flow is the
    // primary path. Manual UI creators can untoggle to opt out
    // permanently. There is no path to flip this back ON later.
    ce_integration_enabled: true,
  });

  useEffect(() => {
    fetchProjects();
    getPMList().then(setPMList).catch(console.error);
    getAllProjectsAlerts().then(setProjectAlerts).catch(console.error);
    getAllDependencyAlerts().then(setDepAlerts).catch(console.error);
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await getProjects({ exclude_status: 'Pre-Construction' });
      setProjects(data);
      setError(null);
    } catch (err) {
      setError('Failed to load projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Project number auto-formatter: XXXXX-X-XX (5 chars, hyphen, 1 letter, hyphen, 2 digits)
  const formatProjectNumber = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

    // Build the formatted string: XXXXX-X-XX
    let formatted = '';
    for (let i = 0; i < Math.min(cleaned.length, 8); i++) {
      if (i === 5) formatted += '-';
      if (i === 6) formatted += '-';
      formatted += cleaned[i];
    }

    return formatted;
  };

  // Validate project number format: XXXXX-X-XX
  const isValidProjectNumber = (value) => {
    const pattern = /^[A-Z]{5}-[A-Z]{1}-\d{2}$/;
    return pattern.test(value);
  };

  const handleProjectNumberChange = (e) => {
    let { value } = e.target;
    // Auto-format as user types
    const formatted = formatProjectNumber(value);
    setFormData((prev) => ({ ...prev, project_number: formatted }));

    // Validate format (but don't show error for partial input)
    if (formatted.length === 10) {
      if (!isValidProjectNumber(formatted)) {
        setProjectNumberError('Format must be XXXXX-X-XX (e.g., ALLEN-D-01)');
      } else {
        setProjectNumberError('');
      }
    } else {
      setProjectNumberError('');
    }
  };

  // Currency input: strips non-numeric chars, stores raw number, displays formatted
  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    // Strip everything except digits and decimal point
    const raw = value.replace(/[^0-9.]/g, '');
    // Prevent multiple decimal points
    const parts = raw.split('.');
    const cleaned = parts[0] + (parts.length > 1 ? '.' + parts.slice(1).join('') : '');
    setFormData((prev) => ({ ...prev, [name]: cleaned }));
  };

  const formatCurrencyInput = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    // Format with commas but keep the raw decimal if user is still typing
    const parts = value.split('.');
    const intPart = parseInt(parts[0] || '0', 10).toLocaleString('en-US');
    if (parts.length > 1) {
      return intPart + '.' + parts[1];
    }
    return intPart;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (creatingProject) return;
    if (!formData.project_name.trim()) {
      setError('Project name is required');
      return;
    }
    if (!(formData.project_manager || '').trim()) {
      setError('At least one project manager is required');
      return;
    }
    try {
      setCreatingProject(true);
      await createProject(formData);
      setFormData({
        project_name: '',
        project_number: '',
        client_name: '',
        original_contract_value: '',
        start_date: '',
        end_date: '',
        project_manager: '',
        construction_manager: '',
        salesperson: '',
        project_developer: '',
        billing_frequency: 'Monthly',
        default_retainage_percent: '',
        description: '',
        client_visible: true,
        ce_integration_enabled: true,
      });
      setProjectNumberError('');
      setShowModal(false);
      await fetchProjects();
      setError(null);
    } catch (err) {
      if (err.code === 'DUPLICATE_PROJECT_NUMBER') {
        setProjectNumberError(err.message);
      } else {
        setError('Failed to create project');
        console.error(err);
      }
    } finally {
      setCreatingProject(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (deletingId) return;
    const project = projects.find(p => p.id === projectId);
    const projectLabel = project?.project_number || project?.project_name || `ID ${projectId}`;

    // Step 1: "Are you sure?"
    const step1 = await confirm({
      title: 'Delete Project',
      message: 'Are you sure you want to delete this project? All associated data (SOV lines, tasks, billing periods, change orders) will be permanently removed.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!step1) return;

    // Step 2: Type project number to confirm
    const step2 = await confirm({
      title: 'Confirm Deletion',
      message: `This cannot be undone. Type the project number below to permanently delete this project.`,
      confirmLabel: 'Permanently Delete',
      danger: true,
      requireTypedConfirmation: projectLabel,
    });
    if (!step2) return;

    try {
      setDeletingId(projectId);
      await deleteProject(projectId);
      await fetchProjects();
      setError(null);
    } catch (err) {
      setError('Failed to delete project');
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Split projects by status: Active and Archived
  const activeStatuses = ['Planning', 'Active', 'On Hold'];
  const archivedStatuses = ['Complete', 'Closed'];

  const activeProjectsList = projects.filter((p) => activeStatuses.includes(p.status));
  const archivedProjectsList = projects.filter((p) => archivedStatuses.includes(p.status));

  // Calculate aggregate stats
  const totalContractValue = projects.reduce(
    (sum, p) => sum + (parseFloat(p.revised_contract_value) || parseFloat(p.contract_amount) || 0),
    0
  );
  const totalBilled = projects.reduce(
    (sum, p) => sum + (parseFloat(p.total_billed) || 0),
    0
  );
  const totalRetainage = projects.reduce(
    (sum, p) => sum + (parseFloat(p.total_retainage) || 0),
    0
  );
  const leftToBill = totalContractValue - totalBilled;
  const activeProjectsCount = activeProjectsList.length;

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>
        Loading projects...
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Dashboard Summary Stats (redesign: 4-card consolidated row) */}
      <DashboardKpiRow
        totalProjects={projects.length}
        activeCount={activeProjectsList.length}
        archivedCount={archivedProjectsList.length}
        totalContractValue={totalContractValue}
        totalBilled={totalBilled}
        totalRetainage={totalRetainage}
        leftToBill={leftToBill}
      />

      {/* Section header: "Active projects" h2 + Active/Archived tabs + view toggle */}
      {projects.length > 0 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginTop: 20,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <h2
              style={{
                margin: 0,
                fontSize: 16.5,
                fontWeight: 600,
                color: 'var(--text-1)',
                letterSpacing: '-0.01em',
              }}
            >
              {showArchived ? 'Archived projects' : 'Active projects'}
            </h2>
            <SegmentedControl
              value={showArchived ? 'archived' : 'active'}
              onChange={(v) => setShowArchived(v === 'archived')}
              options={[
                { value: 'active', label: 'Active', count: activeProjectsList.length },
                { value: 'archived', label: 'Archived', count: archivedProjectsList.length },
              ]}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 12px',
                border: 'none',
                borderRadius: 7,
                background: 'var(--accent)',
                color: 'white',
                fontSize: 14.5,
                fontWeight: 500,
                cursor: 'pointer',
                boxShadow: '0 1px 0 var(--accent-hover) inset, 0 1px 0 rgba(255,255,255,0.2) inset',
                transition: 'background .12s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--accent)')}
            >
              <Icon name="plus" size={14} />
              New project
            </button>
            <SegmentedControl
              compact
              value={viewMode}
              onChange={(v) => {
                setViewMode(v);
                localStorage.setItem('projectViewMode', v);
              }}
              options={[
                {
                  value: 'tiles',
                  icon: <Icon name="grid" size={14} />,
                  title: 'Tile view',
                },
                {
                  value: 'list',
                  icon: <Icon name="list" size={14} />,
                  title: 'List view',
                },
              ]}
            />
          </div>
        </div>
      )}

      {/* Empty-state CTA (no projects at all) keeps a primary action visible */}
      {projects.length === 0 && !loading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 12px',
              border: 'none',
              borderRadius: 7,
              background: 'var(--accent)',
              color: 'white',
              fontSize: 14.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            <Icon name="plus" size={14} />
            New project
          </button>
        </div>
      )}

      {/* Project Cards / List */}
      {loading && projects.length === 0 ? (
        <LoadingState message="Loading projects…" />
      ) : projects.length === 0 ? (
        <StateScreen
          kind="empty"
          title="No projects yet"
          body="Create your first project to start tracking schedules, billing, and contract value."
          primary={{ label: 'Create first project', icon: 'plus', onClick: () => setShowModal(true) }}
        />
      ) : (showArchived ? archivedProjectsList : activeProjectsList).length === 0 ? (
        <StateScreen
          kind="empty"
          title={showArchived ? 'No archived projects' : 'No active projects'}
          body={showArchived
            ? 'Archived projects appear here once you move a project out of active status.'
            : 'Your active project list is empty. Add a new project to get started.'}
          primary={!showArchived ? { label: 'New project', icon: 'plus', onClick: () => setShowModal(true) } : null}
        />
      ) : viewMode === 'tiles' ? (
        /* Redesigned tile view — 3-col grid, ProjectCard per item. */
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: 12,
            marginTop: 16,
          }}
        >
          {(showArchived ? archivedProjectsList : activeProjectsList).map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              alert={projectAlerts[String(project.id)]}
              depAlert={depAlerts[String(project.id)]}
              onSelect={(p) => onSelectProject(p.slug || p.id)}
              onDelete={(p) => handleDelete(p.id)}
              isDeleting={deletingId === project.id}
            />
          ))}
        </div>
      ) : (
        /* Redesigned list view — single ProjectTable with grid rows. */
        <ProjectTable
          projects={showArchived ? archivedProjectsList : activeProjectsList}
          projectAlerts={projectAlerts}
          depAlerts={depAlerts}
          onSelect={(p) => onSelectProject(p.slug || p.id)}
          onDelete={(p) => handleDelete(p.id)}
          deletingId={deletingId}
          totalContractValue={totalContractValue}
          totalBilled={totalBilled}
        />
      )}

      {/* Create Project Modal — token-based ModalShell (matches app theme) */}
      {showModal && (
        <ModalShell
          title="Create New Project"
          subtitle="Set up a project to track schedule, billing, and contract value."
          onClose={() => setShowModal(false)}
          busy={creatingProject}
          width={620}
          footer={(
            <>
              <button
                type="button"
                className="npf-btn npf-btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={creatingProject}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="new-project-form"
                className="npf-btn npf-btn-primary"
                disabled={creatingProject}
              >
                {creatingProject && <span className="npf-spin" />}
                {creatingProject ? 'Creating…' : 'Create Project'}
              </button>
            </>
          )}
        >
          <style>{`
            .npf-form { display:flex; flex-direction:column; gap:16px; }
            .npf-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
            @media (max-width:560px){ .npf-row { grid-template-columns:1fr; } }
            .npf-label { display:block; font-size:12.5px; font-weight:600; color:var(--text-2); letter-spacing:0.01em; margin-bottom:6px; }
            .npf-label .req { color:var(--danger); margin-left:3px; }
            .npf-input, .npf-select, .npf-textarea {
              width:100%; box-sizing:border-box; border-radius:8px;
              border:1px solid var(--border-default); background:var(--bg-surface);
              color:var(--text-1); font-family:var(--font-sans); font-size:14.5px;
              outline:none; transition:border-color 120ms, box-shadow 120ms;
            }
            .npf-input, .npf-select { height:36px; padding:0 12px; }
            .npf-textarea { padding:10px 12px; min-height:74px; resize:vertical; line-height:1.5; }
            .npf-input:focus, .npf-select:focus, .npf-textarea:focus { border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-ring); }
            .npf-input::placeholder, .npf-textarea::placeholder { color:var(--text-4); }
            .npf-cur { position:relative; }
            .npf-cur > span { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--text-3); font-size:14.5px; pointer-events:none; }
            .npf-cur .npf-input { padding-left:26px; }
            .npf-pmbox { border:1px solid var(--border-default); border-radius:8px; padding:8px 10px; background:var(--bg-surface); min-height:36px; }
            .npf-chip { display:inline-flex; align-items:center; gap:6px; padding:3px 10px; border-radius:16px; font-size:13px; font-weight:600; background:var(--accent-soft); color:var(--accent-text); border:1px solid var(--accent-ring); }
            .npf-chip-x { cursor:pointer; font-size:15px; line-height:1; opacity:0.7; }
            .npf-chip-x:hover { opacity:1; }
            .npf-pmselect { width:100%; border:none; background:transparent; color:var(--text-3); font-size:14px; outline:none; cursor:pointer; padding:2px; font-family:var(--font-sans); }
            .npf-err { color:var(--danger); font-size:12.5px; margin-top:5px; }
            .npf-ce { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; border:1px solid var(--border-default); border-radius:10px; background:var(--bg-surface-2); cursor:pointer; }
            .npf-btn { height:34px; padding:0 15px; border-radius:8px; font-size:14px; font-weight:600; cursor:pointer; border:1px solid transparent; display:inline-flex; align-items:center; gap:8px; font-family:var(--font-sans); transition:background .12s, border-color .12s; }
            .npf-btn:disabled { opacity:0.6; cursor:not-allowed; }
            .npf-btn-secondary { background:var(--bg-surface); color:var(--text-2); border-color:var(--border-default); }
            .npf-btn-secondary:not(:disabled):hover { background:var(--bg-surface-2); }
            .npf-btn-primary { background:var(--accent); color:#fff; box-shadow:0 1px 0 var(--accent-hover) inset, 0 1px 0 rgba(255,255,255,0.2) inset; }
            .npf-btn-primary:not(:disabled):hover { background:var(--accent-hover); }
            .npf-spin { width:14px; height:14px; border:2px solid rgba(255,255,255,0.45); border-top-color:#fff; border-radius:50%; animation:npfspin .7s linear infinite; display:inline-block; }
            @keyframes npfspin { to { transform:rotate(360deg); } }
          `}</style>
          <form id="new-project-form" className="npf-form" onSubmit={handleSubmit}>
            <div>
              <label className="npf-label" htmlFor="project_name">Project Name<span className="req">*</span></label>
              <input
                className="npf-input" type="text" id="project_name" name="project_name"
                value={formData.project_name} onChange={handleInputChange} required
                placeholder="e.g., Downtown Office Renovation"
              />
            </div>

            <div className="npf-row">
              <div>
                <label className="npf-label" htmlFor="project_number">Project Number</label>
                <input
                  className="npf-input" type="text" id="project_number" name="project_number"
                  value={formData.project_number} onChange={handleProjectNumberChange}
                  placeholder="XXXXX-X-XX (e.g., ALLEN-D-01)" maxLength="10"
                  style={{ fontFamily: 'var(--font-mono)', letterSpacing: '-0.02em' }}
                />
                {projectNumberError && <div className="npf-err">{projectNumberError}</div>}
              </div>
              <div>
                <label className="npf-label" htmlFor="client_name">Client Name</label>
                <input
                  className="npf-input" type="text" id="client_name" name="client_name"
                  value={formData.client_name} onChange={handleInputChange}
                  placeholder="e.g., ABC Construction"
                />
              </div>
            </div>

            <div className="npf-row">
              <div>
                <label className="npf-label" htmlFor="original_contract_value">Original Contract Value</label>
                <div className="npf-cur">
                  <span>$</span>
                  <input
                    className="npf-input" type="text" id="original_contract_value" name="original_contract_value"
                    value={formatCurrencyInput(formData.original_contract_value)} onChange={handleCurrencyChange}
                    placeholder="0.00"
                    style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
                  />
                </div>
              </div>
              <div>
                <label className="npf-label" htmlFor="project_manager">Project Manager(s)<span className="req">*</span></label>
                <div className="npf-pmbox">
                  {(formData.project_manager || '').split(',').filter(Boolean).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {(formData.project_manager || '').split(',').filter(Boolean).map((pm, i) => (
                        <span key={i} className="npf-chip">
                          {pm.trim()}
                          <span
                            className="npf-chip-x"
                            onClick={() => {
                              const current = (formData.project_manager || '').split(',').filter(Boolean).map((s) => s.trim());
                              setFormData({ ...formData, project_manager: current.filter((_, idx) => idx !== i).join(', ') });
                            }}
                          >&times;</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <select
                    className="npf-pmselect"
                    value=""
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const current = (formData.project_manager || '').split(',').filter(Boolean).map((s) => s.trim());
                      if (!current.includes(e.target.value)) {
                        setFormData({ ...formData, project_manager: [...current, e.target.value].join(', ') });
                      }
                    }}
                  >
                    <option value="">+ Add a project manager…</option>
                    {pmList
                      .filter((pm) => !(formData.project_manager || '').split(',').map((s) => s.trim()).includes(pm.name))
                      .map((pm) => (
                        <option key={pm.id} value={pm.name}>{pm.name} ({pm.role_label || pm.role})</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="npf-row">
              <div>
                <label className="npf-label" htmlFor="construction_manager">Construction Manager</label>
                <select className="npf-input" id="construction_manager" name="construction_manager" value={formData.construction_manager} onChange={handleInputChange}>
                  <option value="">— Unassigned —</option>
                  {pmList.map((pm) => (
                    <option key={pm.id || pm.name} value={pm.name}>{pm.name}{pm.role_label ? ` · ${pm.role_label}` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="npf-label" htmlFor="salesperson">Salesperson</label>
                <select className="npf-input" id="salesperson" name="salesperson" value={formData.salesperson} onChange={handleInputChange}>
                  <option value="">— Unassigned —</option>
                  {pmList.map((pm) => (
                    <option key={pm.id || pm.name} value={pm.name}>{pm.name}{pm.role_label ? ` · ${pm.role_label}` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="npf-label" htmlFor="project_developer">Project Developer</label>
                <select className="npf-input" id="project_developer" name="project_developer" value={formData.project_developer} onChange={handleInputChange}>
                  <option value="">— Unassigned —</option>
                  {pmList.map((pm) => (
                    <option key={pm.id || pm.name} value={pm.name}>{pm.name}{pm.role_label ? ` · ${pm.role_label}` : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="npf-row">
              <div>
                <label className="npf-label" htmlFor="start_date">Start Date</label>
                <input className="npf-input" type="date" id="start_date" name="start_date" value={formData.start_date} onChange={handleInputChange} />
              </div>
              <div>
                <label className="npf-label" htmlFor="end_date">End Date</label>
                <input className="npf-input" type="date" id="end_date" name="end_date" value={formData.end_date} onChange={handleInputChange} />
              </div>
            </div>

            <div className="npf-row">
              <div>
                <label className="npf-label" htmlFor="billing_frequency">Billing Frequency</label>
                <select className="npf-select" id="billing_frequency" name="billing_frequency" value={formData.billing_frequency} onChange={handleInputChange}>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                </select>
              </div>
              <div>
                <label className="npf-label" htmlFor="default_retainage_percent">Default Retainage %</label>
                <input
                  className="npf-input" type="number" id="default_retainage_percent" name="default_retainage_percent"
                  value={formData.default_retainage_percent} onChange={handleInputChange}
                  step="0.01" min="0" max="100" placeholder="e.g., 10"
                />
              </div>
            </div>

            <div>
              <label className="npf-label" htmlFor="description">Description</label>
              <textarea
                className="npf-textarea" id="description" name="description"
                value={formData.description} onChange={handleInputChange} rows="3"
                placeholder="Brief project description…"
              />
            </div>

            {/*
              ComputerEase integration toggle. Set ONCE at project creation.
              Default ON because the sales-tool flow is the primary path. If
              unchecked, billing cycles will NOT submit CREATE_SOV / UPDATE_SOV
              to CE Agent for this project -- billing runs Summit-only. There is
              no path to re-enable later (per Duncan: 'a one time setting'); the
              server PUT route ignores changes to this field after creation.
            */}
            <label className="npf-ce" htmlFor="ce_integration_enabled">
              <input
                type="checkbox" id="ce_integration_enabled" name="ce_integration_enabled"
                checked={!!formData.ce_integration_enabled}
                onChange={(e) => setFormData((prev) => ({ ...prev, ce_integration_enabled: e.target.checked }))}
                style={{ marginTop: 2, width: 15, height: 15, flex: '0 0 auto', accentColor: 'var(--accent)', cursor: 'pointer' }}
              />
              <span>
                <strong style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>Enable ComputerEase integration</strong>
                <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>
                  When checked, billing cycles for this project sync to ComputerEase (CREATE_SOV on first
                  billing, UPDATE_SOV thereafter). This setting can't be changed after the project is
                  created — if you turn it off and later need CE integration, you'll have to create a new project.
                </div>
              </span>
            </label>
          </form>
        </ModalShell>
      )}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default ProjectList;
